import { z } from 'zod';
import { nonNegativeDecimalString as decimalString } from './common';

/**
 * Schemas for the global `SystemSetting` singleton.
 *
 * Holds workshop branding, defaults, and the pricing-engine constants ported
 * from the legacy desktop app: hourly labor rate, per-operation linear-metre
 * rates, join-type CNC/assembly minutes, hole seconds, glass sanding markup,
 * and RON/EUR conversion. All editable by ADMIN only.
 *
 * Endpoints:
 *   GET   /api/v1/admin/settings   -> SETTINGS_READ   (ADMIN only by policy)
 *   PATCH /api/v1/admin/settings   -> SETTINGS_UPDATE (ADMIN only by policy)
 */

export const currencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type SettingsCurrency = z.infer<typeof currencyEnum>;

export const systemSettings = z.object({
  workshopName: z.string(),
  defaultCurrency: currencyEnum,
  defaultLocale: z.string(),
  defaultVatRate: decimalString,

  // Pricing engine
  laborHourlyRateRon: decimalString,
  rateCircularMinPerM: decimalString,
  rateCantMinPerM: decimalString,
  cncEuroscrewMin: decimalString,
  cncDemontabilMin: decimalString,
  assemblyClamexMin: decimalString,
  assemblyMiter45Min: decimalString,
  miter45CircularExtraMin: decimalString,
  holeSecondsEach: z.number().int().nonnegative(),
  glassSandingMarkup: decimalString,
  ronPerEur: decimalString,

  updatedAt: z.string(),
});
export type SystemSettings = z.infer<typeof systemSettings>;

const positiveDecimal = decimalString;

export const updateSystemSettingsRequest = z
  .object({
    workshopName: z.string().trim().min(1).max(200),
    defaultCurrency: currencyEnum,
    defaultLocale: z.string().trim().min(2).max(20),
    defaultVatRate: positiveDecimal,

    laborHourlyRateRon: positiveDecimal,
    rateCircularMinPerM: positiveDecimal,
    rateCantMinPerM: positiveDecimal,
    cncEuroscrewMin: positiveDecimal,
    cncDemontabilMin: positiveDecimal,
    assemblyClamexMin: positiveDecimal,
    assemblyMiter45Min: positiveDecimal,
    miter45CircularExtraMin: positiveDecimal,
    holeSecondsEach: z.number().int().nonnegative().max(86400),
    glassSandingMarkup: positiveDecimal,
    ronPerEur: positiveDecimal,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided.',
  });
export type UpdateSystemSettingsRequest = z.infer<typeof updateSystemSettingsRequest>;
